<template>
  <div class="p-2 bgCommon">
    <!-- QR Code 生成模態框 -->
    <b-modal
      id="modal-generate-qr"
      centered
      title="生成設備綁定 QR Code"
      size="lg"
      hide-footer
    >
      <div v-if="qrCodeData">
        <div class="text-center mb-3">
          <h5>{{ qrCodeData.supplier.company_name_cn }}</h5>
          <p class="text-muted">請使用手機掃描以下 QR Code 進行設備綁定</p>
        </div>
        <div class="text-center mb-3" v-html="qrCodeData.qr_code"></div>
        <div class="text-center">
          <small class="text-muted">
            QR Code 將在 {{ formatDateTime(qrCodeData.expires_at) }} 過期
          </small>
        </div>
      </div>
      <div v-else class="text-center">
        <b-spinner variant="primary"></b-spinner>
        <p class="mt-2">正在生成 QR Code...</p>
      </div>
    </b-modal>

    <!-- 解綁確認模態框 -->
    <b-modal
      id="modal-unbind-confirm"
      centered
      title="確認解除設備綁定"
      ok-title="確認解除"
      cancel-title="取消"
      ok-variant="danger"
      @ok="confirmUnbind"
    >
      <p>確定要解除此設備的綁定嗎？解除後該設備將無法訪問系統。</p>
      <div v-if="selectedBinding">
        <strong>供應商：</strong>{{ selectedBinding.supplier.company_name_cn }}<br>
        <strong>設備：</strong>{{ selectedBinding.device_summary }}<br>
        <strong>綁定時間：</strong>{{ formatDateTime(selectedBinding.bound_at) }}
      </div>
    </b-modal>

    <!-- 頁面標題和操作按鈕 -->
    <div class="d-flex flex-wrap mt-2 mb-1 align-items-center justify-content-between">
      <div class="d-flex flex-wrap align-items-center mobile-w100">
        <h4 class="mb-0">設備綁定管理</h4>
      </div>
      <div class="d-flex flex-wrap align-items-center">
        <b-button
          variant="primary"
          @click="showStatistics"
          class="mr-2"
        >
          <feather-icon icon="BarChartIcon" size="16" class="mr-1" />
          統計信息
        </b-button>
        <b-button
          variant="success"
          @click="refreshData"
        >
          <feather-icon icon="RefreshCwIcon" size="16" class="mr-1" />
          刷新
        </b-button>
      </div>
    </div>

    <!-- 搜索和篩選 -->
    <b-card>
      <b-row>
        <b-col md="4">
          <b-form-group label="搜索供應商">
            <b-form-input
              v-model="searchQuery"
              placeholder="輸入供應商名稱或郵箱"
              @input="handleSearch"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="綁定狀態">
            <v-select
              v-model="statusFilter"
              :options="statusOptions"
              placeholder="選擇狀態"
              @input="handleFilter"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="供應商">
            <v-select
              v-model="supplierFilter"
              :options="supplierOptions"
              placeholder="選擇供應商"
              label="company_name_cn"
              @input="handleFilter"
            />
          </b-form-group>
        </b-col>
        <b-col md="2" class="d-flex align-items-end">
          <b-button
            variant="outline-secondary"
            @click="clearFilters"
            class="mb-3"
          >
            清除篩選
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <!-- 設備綁定列表 -->
    <b-card class="mt-3">
      <b-overlay :show="loading" rounded="sm">
        <b-table
          ref="deviceTable"
          :items="deviceBindings"
          :fields="tableFields"
          :per-page="perPage"
          :current-page="currentPage"
          responsive
          hover
          show-empty
          empty-text="暫無設備綁定記錄"
        >
          <template #cell(supplier)="data">
            <div>
              <strong>{{ data.item.supplier.company_name_cn }}</strong><br>
              <small class="text-muted">{{ data.item.supplier.email }}</small>
            </div>
          </template>

          <template #cell(device_info)="data">
            <div>
              <strong>設備 ID：</strong>{{ data.item.device_id.substring(0, 16) }}...<br>
              <small class="text-muted">{{ data.item.device_summary || '未知設備' }}</small>
            </div>
          </template>

          <template #cell(is_active)="data">
            <b-badge
              :variant="data.item.is_active ? 'success' : 'secondary'"
            >
              {{ data.item.is_active ? '活躍' : '已解綁' }}
            </b-badge>
          </template>

          <template #cell(bound_at)="data">
            {{ formatDateTime(data.item.bound_at) }}
          </template>

          <template #cell(last_accessed_at)="data">
            <div v-if="data.item.last_accessed_at">
              {{ formatDateTime(data.item.last_accessed_at) }}
              <br>
              <small class="text-muted">
                {{ getTimeAgo(data.item.last_accessed_at) }}
              </small>
            </div>
            <span v-else class="text-muted">從未訪問</span>
          </template>

          <template #cell(actions)="data">
            <b-dropdown
              variant="link"
              no-caret
              right
            >
              <template #button-content>
                <feather-icon icon="MoreVerticalIcon" size="16" />
              </template>
              
              <b-dropdown-item
                v-if="!data.item.is_active"
                @click="generateQrCode(data.item.supplier_id)"
              >
                <feather-icon icon="QrCodeIcon" size="16" class="mr-1" />
                生成 QR Code
              </b-dropdown-item>
              
              <b-dropdown-item
                v-if="data.item.is_active"
                @click="showUnbindModal(data.item)"
                variant="danger"
              >
                <feather-icon icon="XCircleIcon" size="16" class="mr-1" />
                解除綁定
              </b-dropdown-item>
              
              <b-dropdown-item
                @click="viewDetails(data.item)"
              >
                <feather-icon icon="EyeIcon" size="16" class="mr-1" />
                查看詳情
              </b-dropdown-item>
            </b-dropdown>
          </template>
        </b-table>

        <!-- 分頁 -->
        <b-pagination
          v-if="totalRows > perPage"
          v-model="currentPage"
          :total-rows="totalRows"
          :per-page="perPage"
          align="center"
          class="mt-3"
        />
      </b-overlay>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BButton,
  BTable,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
  BOverlay,
  BModal,
  BSpinner
} from "bootstrap-vue";
import vSelect from "vue-select";
import { ref, onMounted } from "@vue/composition-api";
import useDeviceManagement from "./useDeviceManagement";

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BButton,
    BTable,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BOverlay,
    BModal,
    BSpinner,
    vSelect
  },
  setup() {
    const {
      // 數據
      deviceBindings,
      loading,
      searchQuery,
      statusFilter,
      supplierFilter,
      statusOptions,
      supplierOptions,
      currentPage,
      perPage,
      totalRows,
      qrCodeData,
      selectedBinding,
      
      // 表格配置
      tableFields,
      
      // 方法
      fetchDeviceBindings,
      generateQrCode,
      unbindDevice,
      handleSearch,
      handleFilter,
      clearFilters,
      refreshData,
      showUnbindModal,
      confirmUnbind,
      viewDetails,
      showStatistics,
      formatDateTime,
      getTimeAgo
    } = useDeviceManagement();

    onMounted(() => {
      fetchDeviceBindings();
    });

    return {
      // 數據
      deviceBindings,
      loading,
      searchQuery,
      statusFilter,
      supplierFilter,
      statusOptions,
      supplierOptions,
      currentPage,
      perPage,
      totalRows,
      qrCodeData,
      selectedBinding,
      
      // 表格配置
      tableFields,
      
      // 方法
      fetchDeviceBindings,
      generateQrCode,
      unbindDevice,
      handleSearch,
      handleFilter,
      clearFilters,
      refreshData,
      showUnbindModal,
      confirmUnbind,
      viewDetails,
      showStatistics,
      formatDateTime,
      getTimeAgo
    };
  }
};
</script>

<style scoped>
.bgCommon {
  background-color: #f8f9fa;
}

.mobile-w100 {
  width: 100%;
}

@media (min-width: 768px) {
  .mobile-w100 {
    width: auto;
  }
}
</style>
